import { Injectable } from '@angular/core';
import { UpgradeModule } from '@angular/upgrade/static';
import { GridRow } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { GraphMailService } from '@app/modules/microsoft-graph/services/graph-mail.service';
import { TypeaheadService } from '@app/services/typeahead.service';
import { Attachment, FileAttachment, ItemAttachment, Message, ReferenceAttachment } from '@microsoft/microsoft-graph-types';
import { BehaviorSubject } from 'rxjs';
/**
 * Shared email data access for the mailbox page, archive forms and reusable email/document viewers.
 * Combines Graph message mapping/enrichment with archived-email operations.
 * Callers own rendering, loading/error UI, confirmation and navigation.
 * The legacy base64 dependency is resolved lazily, only when Graph/archive serialization needs it.
 */
@Injectable({
    providedIn: 'root',
})
export class EmailService {
    /** Mailbox folder readiness shared by the existing folder tree and mailbox page. */
    public initialMailFoldersLoaded$ = new BehaviorSubject(false);

    private base64?: any;

    constructor(
        private datatask: DataTaskService,
        private typeahead: TypeaheadService,
        private graphMail: GraphMailService,
        private upgrade: UpgradeModule,
    ) {}

    /**
     * Enriches the emails given in the parameters according to the settings given in the parameters.
     *
     * @datatask 2950
     * @datataskName
     * @param params the emails and settings to use when enriching.
     * @returns a promise resolving in a list of enriched emails.
     */
    public async enrichEmails(params: EnrichEmailsParams): Promise<EnrichedEmail[]> {
        const chunks = this.getEmailChunks(params.emails, params.chunkSize);

        let data = await Promise.all(chunks.map((chunk) => this.datatask.Post(2950, { ...params, emails: chunk }) as Promise<EnrichedEmail[]>));
        let flatdata = data.flat();
        // return flatdata;
        return flatdata.map((item) => {
            let mailinfo = item.compressed_mailinfo;

            try {
                mailinfo = this.getBase64().urldecode(mailinfo);
            } catch (error) {
                //
            }

            return { ...item, compressed_mailinfo: mailinfo };
        });
    }

    /**
     * Enriches the given emails according to the settings of the parameters, and merges the enriched email to the messages post enriching.
     *
     * @param params the emails and settings to use for enriching.
     * @returns a promise resolving in a list of where enriched emails and messages are merges based on their id.
     */
    public async getEnrichedMessages(params: EnrichEmailsParams): Promise<EnrichedMessage[]> {
        const enrichedEmails = await this.enrichEmails(params);

        return params.emails.map((message) => {
            const email = enrichedEmails.find((enrichedEmail) => enrichedEmail.id === message.id);

            if (!!email) {
                console.table(message);
                console.log(message.datetime_sent);
                console.log(this.getDateInLocalTime(message.datetime_sent ?? ''));
                return { ...message, ...email };
                // return { ...message, ...email, datetime_sent: this.getDateInLocalTime(message.datetime_sent ?? ''), datetime_received: this.getDateInLocalTime(message.datetime_received ?? '') };
            }

            throw Error(`Could not find enriched email matching the id of "${message.id}"`);
        });
    }

    /**
     * Splits the given emails into chunks of emails based on the given chunck size.
     *
     * @param emails the emails to split into chunks.
     * @param chunkSize the max size of each chunks.
     * @returns a list of email chunks.
     */
    private getEmailChunks(emails: Partial<MessageEmail>[], chunkSize: number = 500): Partial<MessageEmail>[][] {
        let chunks: Partial<MessageEmail>[][] = [];
        let numberOfChunks = 0;
        let deepCopyEmails = emails.map(this.serializeMessageEmail);

        while (deepCopyEmails.length > 0) {
            chunks[numberOfChunks] = deepCopyEmails.splice(0, chunkSize);
            numberOfChunks++;
        }

        return chunks;
    }

    /**
     * Creates a deep copy of the given message, and removes unnecessary properties to reduce payload of request calls.
     *
     * @param message the message to serialize before enriching.
     * @returns a deep copy of the message with unecessary properties for enriching removed.
     */
    private serializeMessageEmail = (message: Partial<MessageEmail>) => {
        const email: Partial<MessageEmail> = Object.assign({}, message);
        // delete email.email_body;
        delete email.email_body_html;
        delete email.webLink;
        return email;
    };

    /**
     * Retrieves messages from microsoft graph api and maps them into a TouchTime friendly format.
     *
     * @param params the request settings for retrieving messages from mgt.
     * @returns a promise resolving in a TouchTime version of microsoft messages.
     */
    public async getMessageEmails(params: GetMessageEmailsParams): Promise<MessageEmail[]> {
        const data = (await this.getEmailsFromMgt(params)).map(this.mapMessageToEmail);

        return data;
    }

    /**
     * Retrieves messages from microsoft.
     *
     * @param param0 request settings to use for retrieving messages from microsoft.
     * @returns a promise resolving to a list of message.
     */
    private async getEmailsFromMgt({ folderId, limit, userId, filter, orderBy, search }: GetMessageEmailsParams): Promise<Message[]> {
        console.log('getEmailsFromMgt');
        let messages: Message[] = [];

        try {
            if (!folderId && !!search) {
                // no local search is no folderId.
                messages = (await this.graphMail.getAllMessages({ limit: limit, userId: userId, filter: '', search: `"${search}"` })).value;
            } else if (!!folderId && !!search) {
                messages = (await this.graphMail.getMessagesFromFolder({ folderId: folderId, limit: limit, userId: userId, filter: filter, search: `"${search}"`, orderBy: orderBy ?? '' })).value;
            } else if (!!folderId) {
                const orderby = !!filter.includes('inferenceClassification') ? 'inferenceClassification' : '';
                messages = (await this.graphMail.getMessagesFromFolder({ folderId: folderId, limit: limit, userId: userId, search: '', filter: filter ?? '', orderBy: orderBy ?? orderby })).value;
            }
        } catch (error) {
            console.error(error);
            messages = [];
        }

        return messages;
    }

    private mapMessageToEmail = (message: Message): MessageEmail => {
        let recipientEmail = '';
        let recipientName = '';
        let ccRecipients = '';

        message?.toRecipients?.forEach((recipient) => {
            recipientEmail += recipient?.emailAddress?.address + ', ';
            recipientName += recipient?.emailAddress?.name + ', ';
        });

        message?.ccRecipients?.forEach((email) => (ccRecipients += email?.emailAddress?.address + ', '));

        return {
            id: message.id!,
            recipient_email: recipientEmail.substring(0, recipientEmail.length - 2),
            recipient_name: recipientName.substring(0, recipientName.length - 2),
            email_cc: ccRecipients?.substring(0, ccRecipients.length - 2),
            heading: message.subject ?? '',
            email_body: message.bodyPreview /*? this.getBase64().urlencode(message.bodyPreview) :*/ ?? '',
            email_body_html: !!message.body?.content ? this.getBase64().urlencode(message.body.content) : '',
            webLink: message.webLink ?? '',
            hasAttachments: message.hasAttachments ?? false,
            datetime_sent: this.getDateInLocalTime(message.sentDateTime ?? ''),
            datetime_received: this.getDateInLocalTime(message.receivedDateTime ?? ''),
            read_ok: message.isRead ?? false,
            is_selected: false,
            sender_email: message?.from?.emailAddress?.address ?? '',
            sender_name: message?.from?.emailAddress?.name ?? '',
            importance: message.importance ?? 'normal',
            conversationId: message.conversationId ?? '',
            sentDateTimeZulu: message.sentDateTime ?? '',
        };
    };

    private getDateInLocalTime(datestring?: string | null) {
        if (!!datestring && new Date(datestring).toString() !== 'Invalid Date') {
            const date = new Date(datestring);
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
        }
        return '';
    }

    /**
     * Retrieves mailbox page settings.
     * @datatask 2942
     * @datataskName
     * @returns The first settings row supplied by the procedure.
     */
    public async getEmailPageLoad(): Promise<EmailGet> {
        return (await this.datatask.Post(2942))[0];
    }

    /**
     * Archives the supplied enriched email using the existing serialization contract.
     * @datatask 2951
     * @datataskName
     * @param email Enriched email payload prepared by the caller.
     * @returns The first archive result, including its error code and resulting identifiers.
     */
    public async saveEmailItem(email: EnrichedEmail): Promise<{ errorcode: string; errormessage: string; email_keyno_new: string | number; order_internal_no: string | number }> {
        return (await this.datatask.Post(2951, email))[0];
    }

    public serializeEmail(emailToSave: EnrichedMessage) {
        if (!!emailToSave) {
            let email: any = Object.assign({}, { ...emailToSave, email_body: this.getBase64().urlencode(emailToSave.email_body), heading: this.getBase64().urlencode(emailToSave.heading), custact_note_new: this.getBase64().urlencode(emailToSave.custact_note_new) });
            ['_events', '_handlers', 'parent', 'dirty', '_dirty', 'dirtyFields', 'uid', '_uuid', '_saveFailed'].forEach((key) => delete email[key]);
            return email;
        }

        throw Error('Email must be defined');
    }

    /**
     * Retrieves shared mailboxes available for the supplied account.
     * @datatask 2957
     * @datataskName
     * @param emailAccount Current account email address.
     * @returns Shared mailbox selection rows.
     */
    public getSharedMailboxes(emailAccount: string): Promise<Item[]> {
        return this.datatask.Post(2957, { current_account_email: emailAccount });
    }

    /**
     * Retrieves customer/project context for an activity.
     * @datatask 2725
     * @datataskName
     * @param custactNo Activity key, including a stringified database number.
     * @returns The first matching context row.
     */
    public async getCustomerFromCustact(custactNo: string | number): Promise<CustomerFromCustact> {
        return (await this.datatask.Post(2725, { custact_no: custactNo }))[0];
    }

    /**
     * Retrieves project metadata for email archiving.
     * @datatask 905
     * @datataskName
     * @param projectKeyno Project key.
     * @returns The first matching project row.
     */
    public async getProject(projectKeyno: string | number): Promise<Project> {
        return (await this.datatask.Post(905, { project_keyno: projectKeyno }))[0];
    }

    /**
     * Searches active projects, optionally restricted to a customer.
     * @datatask 902
     * @datataskName
     * @param searchtext Project search text.
     * @param custNo Customer key, or '0' to search without a customer restriction.
     * @returns Project selection rows.
     */
    public async getProjects(searchtext: string, custNo: string | number): Promise<Item[]> {
        if (custNo === '0') {
            return this.datatask.Post(902, { bg_projects_activestatus: 'ACTIVE', searchtext: searchtext ?? '', argtype: '0', argvalue: '0' });
        }

        return this.datatask.Post(902, { bg_projects_activestatus: 'ACTIVE', searchtext: searchtext ?? '', argtype: 'cust_no', argvalue: custNo ?? '0' });
    }

    /**
     * Retrieves activity participant selection rows without an activity restriction.
     * @datatask 95
     * @datataskName
     * @returns Participant selection rows.
     */
    public getCustactMembers(): Promise<Item[]> {
        return this.datatask.Post(95, { custact_no: '0', user_name: '' });
    }

    public searchAllCustomer(value: string): Promise<Item[]> {
        return this.typeahead.searchCustomer({
            cust_name: value || '',
            custtype: 'A',
            lastselect: 'SEARCH',
        });
    }

    /**
     *
     * @datatask 2772
     * @datataskName sp_ws_p2_ordersearch_typeahead_list
     * @param searchtext
     * @param custNo
     * @returns
     */
    public searchOrders(searchtext: string, custNo?: string | number): Promise<OrderSearchItem[]> {
        return this.datatask.Post(2772, { searchtext: searchtext, cust_no: custNo });
    }

    /**
     *
     *
     * @datatask 2955
     * @datataskName sp_ws_p2_purchhead_typeahead_list
     * @param searchtext
     * @param custNo
     * @returns
     */
    public searchPurchases(searchtext: string, custNo?: string | number): Promise<PurchSearchItem[]> {
        return this.datatask.Post(2955, { searchtext: searchtext, cust_no: custNo });
    }

    /**
     * Retrieves person selection rows for a customer.
     * @datatask 71
     * @datataskName
     * @param custNo Customer key.
     * @returns Person selection rows.
     */
    public getCustomerPersons(custNo: string | number): Promise<Item[]> {
        return this.datatask.Post(71, { cust_no: custNo });
    }

    /**
     * Retrieves the first archived email row for the supplied key.
     * @datatask 1013
     * @datataskName sp_ws_p2_w_emailview_get
     * @param params Email lookup parameters.
     * @returns The email row, or undefined when the procedure returns no rows.
     * Request failures reject the promise; attachments are retrieved separately.
     */
    public async getEmail(params: GetEmailParams): Promise<EmailViewerMessage | undefined> {
        return (await this.datatask.Post(1013, params))[0];
    }

    /**
     * Retrieves attachment metadata without downloading file content.
     * @datatask 1014
     * @datataskName sp_ws_p2_w_emailview_attatchments_list
     * @param params Attachment lookup parameters for the archived email.
     * @returns Attachment rows in the order supplied by the procedure; request failures reject.
     */
    public getAttachments(params: GetEmailAttachmentsParams): Promise<EmailViewerAttachment[]> {
        return this.datatask.Post(1014, params);
    }

    /**
     * Retrieves reply navigation entries for an archived email.
     * @datatask 2963
     * @datataskName
     * @param params Email key used to look up replies.
     * @returns Reply rows; request failures reject the promise.
     */
    public getReplies(params: GetEmailRepliesParams): Promise<EmailViewReply[]> {
        return this.datatask.Post(2963, params);
    }

    /**
     * Deletes the supplied archived email after the caller has obtained user confirmation.
     * @datatask 3002
     * @datataskName
     * @param params Archived email row passed through using the existing procedure contract.
     * @returns The first result row, or undefined for no rows. Callers must inspect errorcode;
     * procedure error rows are returned as data, while request failures reject the promise.
     */
    public async deleteEmail(params: EmailViewGet): Promise<EmailViewDeleteResponse | undefined> {
        return (await this.datatask.Post(3002, params))[0];
    }

    public getBase64(): any {
        if (!this.base64) {
            const $injector = this.upgrade.injector.get('$injector');
            this.base64 = $injector.get('base64');
        }
        return this.base64;
    }
}
export interface GetMessageEmailsParams {
    /**
     * No id provided will query all emails.
     */
    folderId?: string;
    limit: number;
    userId?: string;
    filter: string;
    orderBy?: string;
    search: string;
}
export interface EnrichEmailsParams {
    /**
     * The currently selected businessco.
     */
    businessco_no: string;

    /**
     * The currently used email account.
     */
    current_account_email: string;

    /**
     * List of messages parsed to Touchtime format that are to be enriched with additional info from TouchTime.
     */
    emails: MessageEmail[];

    /**
     * The size to split the emails sent to the enrich call.
     *
     * @default 500
     */
    chunkSize?: number;
}

export interface EmailGet {
    current_account_email: string;
    retrieve_mail_from_dt: string;
    selected_folder_name: string;
    selected_folder_id: string;
    auto_archieve_folder_id: string;
    auto_archieve_folder_name: string;
    move_to_folder_id: string;
    move_to_folder_name: string;
    current_mailbox: string;
    retrieve_max_nbr_of_emails: string;
    show_buttons_accounting: '1' | '0';
    heading_email_archiving: string;
    email_search_global: string;
    local_search: '1' | '0';
    businessco_no: string;
    show_setting: '1' | '0';
    folders_width: string;
    folders_is_expanded: '1' | '0';
    gb_email_main: string;
}

export interface MessageEmail {
    /**
     * Unique id for this email.
     */
    id: string;

    /**
     * The recipients emails, either a single one or multiple in a string seperated by `', '`.
     */
    recipient_email: string;

    /**
     * The recipients names, either a single one or multiple in a string seperated by `', '`.
     */
    recipient_name: string;
    /**
     * Emails addresses added on as copy, either a single one or multiple in a string seperated by `', '`.
     */
    email_cc: string;

    /**
     * The heading of the email.
     */
    heading: string;

    /**
     * A short preview of the email contents.
     */
    email_body: string;

    /**
     * Base64 encoded content of the email.
     */
    email_body_html: string;

    /**
     * Link to open the email in a webview for office 365.
     */
    webLink: string;

    /**
     * Whether the email has any **non-inline** attachments.
     */
    hasAttachments: boolean;

    /**
     * The timestamp for the when the email was sent.
     */
    datetime_sent: string;

    /**
     * The timestamp for the when the email was received.
     */
    datetime_received: string;

    /**
     * Whether the email is marked as read.
     */
    read_ok: boolean;

    /**
     * Whether the email has been selected.
     */
    is_selected: boolean;

    /**
     * The email of the sender.
     */
    sender_email: string;

    /**
     * The name of the sender.
     */
    sender_name: string;

    /**
     * The importance of the email.
     */
    importance: 'low' | 'normal' | 'high';

    /**
     * The id of the conversation (email thread) this email belongs to.
     */
    conversationId: string;

    /**
     * The string of the sentdatetime used to identify the grid.
     */
    sentDateTimeZulu: string;
}
export interface EnrichedEmail {
    /**
     * Unique id for this email.
     */
    id: string;
    cust_no: string | number;
    cust_name: string;
    pers_no: string;
    pers_name: string;
    custact_no: string | number;
    custact_note: string;
    create_new_custact: '1' | '0';
    custact_note_new: string;
    activitytype_no: string;
    project_keyno: string | number;
    project_name: string;
    projectprosess_keyno: string | number;
    prosess_name: string;
    order_internal_no: string | number;
    order_search_info: string;
    purch_internal_no: string | number;
    purch_search_info: string;

    participant_pu_keynos: string;
    participant_pu_names: string;
    compressed_mailinfo: string;

    /**
     * The portal user keyno for the user responsible for the custact on this email.
     */
    portal_user_keyno_responsible: string;

    /**
     * Whether to create a voucher from this email.
     */
    create_voucher: '1' | '0';
    create_offer: '1' | '0';

    /**
     * The id of the customer in touchtime this email was sent from.
     */
    from_cust_no: string | number;

    /**
     * The name of the customer in touchtime this email was sent from.
     */
    from_cust_name: string;

    /**
     * The email of the sender.
     */
    sender_email_new: string;

    /**
     * The name of the sender.
     */
    sender_name_new: string;

    /**
     * Space sepeared list of icons to display for this email in grid.
     */
    item_glyphicon: string;

    /**
     * Space seperated list of color to use for the space seperated icon list.
     */
    item_glyphicon_color: string;

    /**
     * Id of the company this email may be related to.
     */
    businessco_no: string;

    exist_in_email_archive: '1' | '0' | 1 | 0;

    email_keyno: `${number}` | number;

    /**
     * Related to grid use.
     */
    _dirty: false;

    /**
     * Related to grid use.
     */
    _uuid: string;
}

export type EnrichedMessage = EnrichedEmail & MessageEmail & GridRow & { attachments?: (Attachment | FileAttachment | ReferenceAttachment | ItemAttachment)[] };

export interface Project {
    project_keyno: string | number;
    project_name: string;
    active: '1' | '0';
    cust_no: string | number;
    cust_name: string;
    businessco_no: string | number;
    description: string;
    nbr_of_projectprosess: string | number;
    activities_badge: string;
    orders_badge: string;
    sum_ordervalue_locval: string | number;
    sum_purchasevalue_locval: string | number;
    sum_hours_total: string | number;
    purchases_badge: string;
    nbr_of_documents: string | number;
    nbr_of_pictures: string | number;
    show_button_pictures: '1' | '0';
    show_button_documents: '1' | '0';
    show_button_timesheetdiary: '1' | '0';
    show_button_gantt: '1' | '0';
    show_button_custactplanning: '1' | '0';
    show_paymentplans: '1' | '0';
    webpage_heading: string;
    gb_projectmenu_main: string;
    nbr_of_docpacktypes: string | number;
    docpacktype_keyno: string | number;
}

export interface CustomerFromCustact {
    cust_no: string;
    cust_name: string;
    project_keyno: string;
    projectprosess_keyno: string;
    custact_note: string;
    powtime_description: string;
    po_keyno: string;
    activitytype_no: string;
    actstatus_keyno: string;
    custact_no: string;
    project_name: string;
    prosess_name: string;
}

export interface EmailArchivedEvent {
    email: EnrichedMessage;
    email_keyno_new: string | number;
    errorcode: string;
    errormessage: string;
    goto_reply: '1' | '0';
    reply_all: '1' | '0';
    order_internal_no: string | number;
    event: MouseEvent;
}

// All types are currently `string` because all? return data from database is string
export interface OrderSearchItem {
    order_internal_no: string;
    item_name: string;
    orderby: string;
    cust_no: string;
    cust_name: string;
    project_keyno: string;
    project_name: string;
    order_order_no: string;
    order_offer_no: string;
}

export interface PurchSearchItem {
    purch_internal_no: string;
    item_name: string;
    orderby: string;
    cust_no: string;
    cust_no_name: string;
}

/** Parameters accepted by the archived-email lookup. */
export interface GetEmailParams {
    email_keyno: string | number;
}

/** Parameters accepted by the archived-email attachment lookup. */
export interface GetEmailAttachmentsParams {
    email_keyno: string | number;
}

/** Parameters accepted by the archived-email reply lookup. */
export interface GetEmailRepliesParams {
    email_keyno: string | number;
}

/** Archived email row. Field names, including attatchment_heading, follow the database contract. */
export interface EmailViewerMessage {
    email_keyno?: string | number;
    recipient_email?: string;
    recipient_name?: string;
    heading?: string;
    body?: string;
    email_body?: string;
    email_body_html?: string;
    sender_email?: string;
    sender_name?: string;
    datetime_sent?: string;
    email_cc?: string;
    attatchment_heading?: string;
}

/** Attachment metadata accepted from archive rows or supplied Graph-style attachment records. */
export interface EmailViewerAttachment {
    id?: string | null;
    item_id?: string | number | null;
    item_name?: string | null;
    item_path?: string | null;
    item_glyphicon?: string | null;
    item_glyphicon_color?: string | null;
    item_state?: string | null;
    item_parms?: string | null;
    orderby?: string | number | null;
    name?: string | null;
    contentType?: string | null;
    contentBytes?: string | null;
    size?: number | null;
}

/** Archived email with page action permissions supplied by the procedure. */
export interface EmailViewGet extends EmailViewerMessage {
    show_setting?: '1' | '0';
    show_delete?: '1' | '0';
}

/** Navigation metadata for a reply to an archived email. */
export interface EmailViewReply {
    item_id: string | number;
    item_name: string;
    item_state?: string;
    item_parms?: string;
    item_glyphicon?: string;
    item_glyphicon_color?: string;
    [key: string]: unknown;
}

/** Procedure result for email deletion; errorcode '0' indicates success. */
export interface EmailViewDeleteResponse {
    errorcode: string;
    errormessage: string;
}
